import { RolodexDocumentEntry, useLinkedDocuments } from 'features/files'
import { useMemo } from 'react'

import { normalizeCategory } from './helpers/categoryUtils'
import { computeCategoryMetrics } from './helpers/computeCategoryMetrics'

export interface DocumentCategoryMetric {
  category: string
  categoryLabel: string
  count: number
  documentIds: ReadonlyArray<string>
}

export interface DataHealthMetrics {
  categoryMetrics: ReadonlyArray<DocumentCategoryMetric>
  totalDocuments: number
  documents: ReadonlyArray<RolodexDocumentEntry>
  metricsByCategory: ReadonlyMap<string, number>
  documentsByCategory: ReadonlyMap<string, ReadonlyArray<RolodexDocumentEntry>>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => Promise<void>
}

export function useDataHealthMetrics(
  unitId: string | undefined
): DataHealthMetrics {
  const { documents, isLoading, hasEverLoaded, forceRefresh } =
    useLinkedDocuments(unitId ? { unit: unitId } : undefined)

  const categoryMetrics = useMemo(
    () => computeCategoryMetrics(documents),
    [documents]
  )

  const metricsByCategory = useMemo(() => {
    const m = new Map<string, number>()
    categoryMetrics.forEach((metric) => {
      m.set(metric.category, metric.count)
    })
    return m
  }, [categoryMetrics])

  const documentsByCategory = useMemo(() => {
    const m = new Map<string, RolodexDocumentEntry[]>()
    documents.forEach((document) => {
      const normalizedCategories = Array.from(
        new Set(
          (document.categories.length > 0 ? document.categories : ['unknown'])
            .map(normalizeCategory)
            .filter(Boolean)
        )
      )

      normalizedCategories.forEach((category) => {
        const list = m.get(category) ?? []
        list.push(document)
        m.set(category, list)
      })
    })
    return m
  }, [documents])

  const totalDocuments = documents.length

  return {
    categoryMetrics,
    totalDocuments,
    documents,
    metricsByCategory,
    documentsByCategory,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  }
}
